'use client';
import { useEffect, useRef } from 'react';
import drawCylinder from '@/lib/visualisers/cylinder';

interface CylinderVisualiserProps {
    radius: number;
    height: number;
    fillColor: string;
    unit?: string;
}

export default function CylinderVisualiser({
    radius,
    height,
    fillColor,
    unit = '',
}: CylinderVisualiserProps) {
    const canvasRef = useRef<HTMLCanvasElement>(null);

    useEffect(() => {
        const c = canvasRef.current;
        if (!c) return;
        drawCylinder(c, { radius, height, fillColor, unit });
    }, [radius, height, fillColor, unit]);

    const volume = Math.PI * radius * radius * height;

    return (
        <div className="w-full max-w-[460px] overflow-hidden rounded-lg border-[0.5px] border-line bg-white">
            <canvas
                ref={canvasRef}
                aria-label={`3D cylinder with radius ${radius} and height ${height}`}
                className="block h-auto w-full"
            />
            <div className="flex flex-col gap-1.5 border-t-[0.5px] border-line bg-page px-5 py-4">
                <p className="mb-0.5 text-[11px] font-medium uppercase tracking-[0.05em] text-hint">
                    Formula
                </p>
                <p className="text-sm text-muted">V = π × r² × h</p>
                <p className="text-sm text-muted">
                    V = π × {parseFloat(radius.toFixed(4))}² × {parseFloat(height.toFixed(4))}
                </p>
                <p className="mt-1 text-base font-medium text-teal">
                    V ≈ {volume.toFixed(2)} {unit ? `${unit}³` : 'units³'}
                </p>
            </div>
        </div>
    );
}
